/**
 * The automation panel: the taught path's buttons on their own, beside the picture, with the
 * controls still driving the arm (the settings panel stills them, which made teaching a round
 * of shut the menu, move, open it, press, shut it again for every point).
 *
 * The arm takes these actions while teaching and during a pick (pick_loop inputs.on_action);
 * anywhere else the buttons are greyed and the panel says why.
 */
import type { View } from "./menuModel";
import type { Widget } from "./widgets";

/** States the arm takes a path point in (pick_loop constants.TEACH_POINT_STATES). */
const EDITABLE = new Set(["teach", "pick"]);

export function buildAutomation(view: View, clearArmed: boolean): Widget[] {
  const { path, status } = view;
  const state = status?.state ?? null;
  const editable = state !== null && EDITABLE.has(state);
  const teaching = state === "teach";
  const widgets: Widget[] = [
    { kind: "section", text: "Automation" },
    { kind: "note", label: "taught",
      value: `${path.start ? "start" : "no start"} · ${path.waypoints} waypoint${path.waypoints === 1 ? "" : "s"} · ${path.end ? "end" : "no end"}` },
    { kind: "note", label: "", value: hint(view, state, editable) },
    { kind: "buttons", buttons: [
      { id: "set_start", label: path.start ? "Move start here" : "Set start", disabled: !editable },
      { id: "add_waypoint", label: "Add waypoint", disabled: !editable || !path.start },
    ] },
    { kind: "buttons", buttons: [
      { id: "set_end", label: path.end ? "Move end here" : "Set end", disabled: !editable || !path.start },
      // During a pick the arm undoes waypoints only: the start and the end stay while it runs.
      { id: "undo", label: "Undo last point",
        disabled: !editable || (teaching ? !path.start : path.waypoints === 0) },
    ] },
  ];
  if (state === "idle") {
    widgets.push({ kind: "button", id: "enable", label: "Enable the arm", tone: "primary" });
  } else if (teaching) {
    widgets.push({ kind: "button", id: "go", label: "Go  ·  run the automation", tone: "primary",
                   disabled: !path.start || !path.end });
  }
  widgets.push({ kind: "buttons", buttons: [
    { id: "clear_path", label: clearArmed ? "Press again to clear" : "Start over", tone: "warn",
      disabled: state === null || !(editable || state === "blocked") || (!path.start && !path.end) },
    { id: "close_automation", label: "Done" },
  ] });
  return widgets;
}

function hint(view: View, state: string | null, editable: boolean): string {
  const { path } = view;
  if (state === null) return "waiting for the robot";
  if (state === "idle") return "enable the arm to teach it";
  if (!editable) return `wait: the arm is busy (${state.replace(/_/g, " ")})`;
  if (!path.start) return "drive over the pile, then Set start";
  if (!path.end) return "add waypoints on the way to the drop, then Set end";
  return state === "teach" ? "Go runs it · a new waypoint goes before the end" : "a new waypoint goes before the end";
}
